import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import Input from 'components/ui/Input';

export default function CheckoutModal({ isOpen, onClose, total, itemCount }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ email: '', name: '', card: '', expiry: '', cvv: '', zip: '' });
  const [errors, setErrors] = useState({});
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);

  if (!isOpen) return null;

  const handleChange = (field) => (e) => setForm({ ...form, [field]: e?.target?.value });

  const validateStep1 = () => {
    const errs = {};
    if (!form?.email) errs.email = 'Email is required';
    if (!form?.name) errs.name = 'Name is required';
    setErrors(errs);
    return Object.keys(errs)?.length === 0;
  };

  const validateStep2 = () => {
    const errs = {};
    if (!form?.card || form?.card?.length < 16) errs.card = 'Valid card number required';
    if (!form?.expiry) errs.expiry = 'Expiry required';
    if (!form?.cvv || form?.cvv?.length < 3) errs.cvv = 'CVV required';
    if (!form?.zip) errs.zip = 'ZIP required';
    setErrors(errs);
    return Object.keys(errs)?.length === 0;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) setStep(2);
  };

  const handlePay = () => {
    if (!validateStep2()) return;
    setProcessing(true);
    setTimeout(() => { setProcessing(false); setSuccess(true); }, 2000);
  };

  const handleClose = () => { setStep(1); setSuccess(false); setForm({ email: '', name: '', card: '', expiry: '', cvv: '', zip: '' }); onClose(); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,48,87,0.75)' }}>
      <div className="w-full max-w-md rounded-2xl shadow-2xl overflow-hidden" style={{ background: 'var(--color-background)' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-5" style={{ background: 'var(--color-primary)' }}>
          <div className="flex items-center gap-2">
            <Icon name="Lock" size={18} color="var(--color-accent)" />
            <h2 className="font-heading font-semibold text-lg text-white">Secure Checkout</h2>
          </div>
          <button onClick={handleClose} className="w-8 h-8 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(255,255,255,0.1)' }}>
            <Icon name="X" size={16} color="#FFFFFF" />
          </button>
        </div>

        <div className="p-6">
          {success ? (
            <div className="flex flex-col items-center gap-4 py-8 text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(5,150,105,0.1)' }}>
                <Icon name="CheckCircle" size={36} color="var(--color-success)" />
              </div>
              <h3 className="font-heading font-bold text-2xl" style={{ color: 'var(--color-foreground)' }}>Order Confirmed!</h3>
              <p className="font-caption text-sm" style={{ color: 'var(--color-muted-foreground)' }}>
                Thank you for supporting women-owned businesses! Your order of {itemCount} item{itemCount > 1 ? 's' : ''} totaling <strong>${total?.toFixed(2)}</strong> has been placed.
              </p>
              <p className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
                A confirmation email has been sent to {form?.email}
              </p>
              <Button variant="default" onClick={handleClose} fullWidth>Continue Shopping</Button>
            </div>
          ) : (
            <>
              {/* Steps */}
              <div className="flex items-center gap-2 mb-6">
                {[1, 2]?.map((s) => (
                  <React.Fragment key={s}>
                    <div className="flex items-center gap-1.5">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center font-caption text-xs font-bold"
                        style={{ background: step >= s ? 'var(--color-accent)' : 'var(--color-muted)', color: step >= s ? 'var(--color-accent-foreground)' : 'var(--color-muted-foreground)' }}>
                        {s}
                      </div>
                      <span className="font-caption text-xs font-medium" style={{ color: step >= s ? 'var(--color-foreground)' : 'var(--color-muted-foreground)' }}>
                        {s === 1 ? 'Contact' : 'Payment'}
                      </span>
                    </div>
                    {s < 2 && <div className="flex-1 h-px" style={{ background: step > s ? 'var(--color-accent)' : 'var(--color-border)' }} />}
                  </React.Fragment>
                ))}
              </div>

              {/* Order Summary */}
              <div className="p-3 rounded-lg mb-4 flex items-center justify-between"
                style={{ background: 'rgba(133,113,77,0.08)', border: '1px solid rgba(133,113,77,0.2)' }}>
                <span className="font-caption text-sm" style={{ color: 'var(--color-foreground)' }}>
                  {itemCount} item{itemCount > 1 ? 's' : ''}
                </span>
                <span className="font-data font-bold text-lg" style={{ color: 'var(--color-accent)' }}>${total?.toFixed(2)}</span>
              </div>

              {step === 1 ? (
                <div className="space-y-4">
                  <Input label="Full Name" type="text" placeholder="Jane Smith" value={form?.name}
                    onChange={handleChange('name')} error={errors?.name} required />
                  <Input label="Email Address" type="email" placeholder="jane@example.com" value={form?.email}
                    onChange={handleChange('email')} error={errors?.email} required />
                  <Button variant="default" fullWidth onClick={handleNext} iconName="ArrowRight" iconPosition="right">
                    Continue to Payment
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <Input label="Card Number" type="text" placeholder="4242 4242 4242 4242" value={form?.card}
                    onChange={handleChange('card')} error={errors?.card} required maxLength={16} />
                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-1">
                      <Input label="Expiry" type="text" placeholder="MM/YY" value={form?.expiry}
                        onChange={handleChange('expiry')} error={errors?.expiry} required />
                    </div>
                    <div className="col-span-1">
                      <Input label="CVV" type="text" placeholder="123" value={form?.cvv}
                        onChange={handleChange('cvv')} error={errors?.cvv} required maxLength={4} />
                    </div>
                    <div className="col-span-1">
                      <Input label="ZIP" type="text" placeholder="10001" value={form?.zip}
                        onChange={handleChange('zip')} error={errors?.zip} required />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-2 rounded-lg" style={{ background: 'var(--color-muted)' }}>
                    <Icon name="Shield" size={14} color="var(--color-success)" />
                    <span className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
                      Secured by Stripe · SSL Encrypted
                    </span>
                  </div>
                  <div className="flex gap-3">
                    <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
                    <Button variant="default" fullWidth onClick={handlePay} loading={processing}
                      iconName="CreditCard" iconPosition="left">
                      {processing ? 'Processing...' : `Pay $${total?.toFixed(2)}`}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}